import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { prefetchNotificationFeed, useNotificationUnreadCount } from '../../hooks/api';
import { formatBadgeCount } from '../../utils/notificationDisplay';
import NotificationPanel from './NotificationPanel';

const PANEL_ID = 'notification-panel';

/** Ring briefly whenever the unread count rises after the first load. */
const useRingOnIncrease = (count) => {
  const [ringing, setRinging] = useState(false);
  const previous = useRef(null);

  useEffect(() => {
    if (count === undefined) return undefined;
    const last = previous.current;
    previous.current = count;
    if (last === null || count <= last) return undefined;
    setRinging(true);
    const timer = setTimeout(() => setRinging(false), 1000);
    return () => {
      clearTimeout(timer);
      setRinging(false);
    };
  }, [count]);

  return ringing;
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const queryClient = useQueryClient();
  const { data } = useNotificationUnreadCount();
  const unreadCount = data?.unread_count || 0;
  const ringing = useRingOnIncrease(data?.unread_count);

  const close = useCallback(() => setOpen(false), []);
  const prefetch = useCallback(() => {
    prefetchNotificationFeed(queryClient);
  }, [queryClient]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        onMouseEnter={prefetch}
        onFocus={prefetch}
        className={`relative flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
          open
            ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300'
            : 'text-secondary-600 hover:text-primary-600 dark:text-secondary-300'
        }`}
        title="Notifications"
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? PANEL_ID : undefined}
        data-testid="notification-bell"
      >
        <Bell
          className={`h-5 w-5 origin-top ${ringing ? 'motion-safe:animate-bell-ring' : ''}`}
          aria-hidden="true"
        />
        {unreadCount > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white lg:h-5 lg:min-w-[1.25rem]"
            data-testid="notification-badge"
          >
            {formatBadgeCount(unreadCount)}
          </span>
        )}
      </button>
      {open && <NotificationPanel id={PANEL_ID} unreadCount={unreadCount} onClose={close} />}
    </div>
  );
};

export default NotificationBell;
